import { useEffect, useState, useMemo } from "react";
import { useParams, Navigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { Avatar } from "../components/Posts";
import { api, getUser, clinicalLabel, canClinical, fmtDate } from "../api";
import { toast } from "sonner";
import { PdfViewer } from "../components/PdfViewer";
import { DicomViewer, resolveFolderItems } from "../components/DicomViewer";
import { ClinicalDocEditor } from "../components/ClinicalDocEditor";
import { ChevronDown, ChevronRight } from "lucide-react";

export default function ClinicalFolder() {
  const { patientId } = useParams();
  const isInternal = typeof patientId === "string" && patientId.startsWith("ip_");
  const { t, i18n } = useTranslation();
  const me = getUser();
  const [access, setAccess] = useState(null); // null=caricamento, {authorized,...}
  const [folders, setFolders] = useState([]);
  const [patient, setPatient] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [imgViewer, setImgViewer] = useState(null);
  const [opening, setOpening] = useState(null);
  const [editorCtx, setEditorCtx] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [sp, setSp] = useSearchParams();
  const [openId, setOpenId] = useState(null);       // R2-9: accordion — un solo esame aperto per volta
  const [newEp, setNewEp] = useState(null);
  const [q, setQ] = useState("");
  const [period, setPeriod] = useState("all");
  const [typeF, setTypeF] = useState("all");
  const reload = () => api.get(`/patients/${patientId}/shared-exams`).then(({ data }) => setFolders(data)).catch(() => {});
  const uploadDoc = async (fid, file) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) return toast.error("Carica un PDF");
    setUploading(fid);
    const title = window.prompt("Titolo del documento clinico:", file.name.replace(/\.pdf$/i, "")) || file.name;
    const fd = new FormData(); fd.append("file", file); fd.append("title", title); fd.append("publish", "true");
    try { await api.post(`/exams/${fid}/clinical-documents/upload`, fd); toast.success("Documento caricato e pubblicato"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail === "invalid_pdf" ? "File non valido (solo PDF)" : "Errore caricamento"); }
    finally { setUploading(null); }
  };

  useEffect(() => {
    if (!me || me.role === "user") return;
    api.get(`/patients/${patientId}/clinical-access`).then(({ data }) => {
      setAccess(data);
      if (data.authorized && data.patient_name) setPatient({ name: data.patient_name, avatar: data.patient_avatar_url });
    }).catch(() => setAccess({ authorized: false }));
    api.get(`/patients/${patientId}/shared-exams`).then(({ data }) => {
      setFolders(data);
      if (data[0]) setPatient({ name: data[0].patient_name, avatar: data[0].patient_avatar_url });
    }).catch(() => {});
  }, [patientId]); // eslint-disable-line
  useEffect(() => () => { if (viewer?.url) URL.revokeObjectURL(viewer.url); }, [viewer]);
  useEffect(() => {
    const me2 = getUser(); if (!me2 || me2.role === "user") return;
    const ep = sp.get("ep"); if (!ep) return;
    api.post("/clinical-episodes", { patient_id: patientId, episode_type: ep,
      vc_id: sp.get("vc") || undefined, booking_id: sp.get("booking") || undefined,
      service_id: sp.get("service") || undefined, date: sp.get("date") || undefined })
      .then(({ data }) => { setOpenId(data.folder_id); reload(); toast.success("Episodio pronto: scegli Redigi o Carica documento"); })
      .catch((e) => toast.error(e?.response?.data?.detail === "no_relationship" ? "Nessuna relazione con il paziente" : "Errore episodio"))
      .finally(() => { ["ep", "vc", "booking", "service", "date"].forEach((k) => sp.delete(k)); setSp(sp, { replace: true }); });
  }, [patientId]); // eslint-disable-line

  const types = useMemo(() => Array.from(new Set(folders.map((f) => f.exam_type).filter(Boolean))), [folders]);
  const filtered = useMemo(() => {
    const now = Date.now();
    const days = { "7": 7, "30": 30, "365": 365 }[period];
    const qn = q.trim().toLowerCase();
    return [...folders]
      .filter((f) => {
        if (typeF !== "all" && f.exam_type !== typeF) return false;
        if (days && f.study_date) { const d = new Date(f.study_date).getTime(); if (!isNaN(d) && (now - d) > days * 864e5) return false; }
        if (qn) {
          const inTitle = `${f.title || ""} ${f.exam_type || ""}`.toLowerCase().includes(qn);
          const inDocs = (f.documents || []).some((d) => (d.filename || "").toLowerCase().includes(qn));
          if (!inTitle && !inDocs) return false;
        }
        return true;
      })
      .sort((a, b) => String(b.study_date || "").localeCompare(String(a.study_date || "")));  // più recente -> più vecchio
  }, [folders, q, period, typeF]);

  if (!me) return <Navigate to={`/login?next=/clinica/${patientId}`} replace />;
  if (me.role === "user") return <Navigate to="/drive" replace />;
  if (access && !access.authorized) return (<Layout><div className="yp-container py-8" data-testid="clinical-folder">
    <Link to="/drive" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-4" data-testid="clinical-back"><ChevronRight size={14} className="rotate-180" />{t("pd.tab_patients")}</Link>
    <div className="yp-card p-6 text-center" data-testid="clinical-access-denied">
      <h1 className="text-xl font-semibold mb-2" data-testid="clinical-access-denied-title">{t("pd.access_denied_title")}</h1>
      <p className="text-sm text-slate-500" data-testid="clinical-access-denied-desc">{t("pd.access_denied_desc")}</p>
    </div>
  </div></Layout>);

  const openFolder = async (f) => {
    // UNICO YouPAM Clinical Viewer: studio DICOM da PAM Connect + upload raster/DICOM + DICOM legacy, una sola interfaccia.
    const items = await resolveFolderItems(f);
    if (!items.length) { toast.message("Nessuna immagine disponibile"); return; }
    setImgViewer({ items, folderId: f.id });
  };
  const deleteImage = async (fid, objectId) => {
    try {
      await api.delete(`/exams/${fid}/images/${objectId}`);
      setImgViewer((v) => v ? { ...v, items: (v.items || []).filter((im) => im.object_id !== objectId) } : v);
      toast.success("Immagine eliminata"); reload();
    } catch (e) { toast.error(e?.response?.status === 403 ? "Non sei l'autore di questa immagine" : "Errore eliminazione"); }
  };
  const deleteDoc = async (fid, d) => {
    if (!window.confirm("Eliminare definitivamente questo documento? Non sarà più disponibile agli altri utenti.")) return;
    try { await api.delete(`/exams/${fid}/clinical-documents/${d.id}`); toast.success("Documento eliminato"); reload(); }
    catch (e) { toast.error(e?.response?.status === 403 ? "Non sei l'autore di questo documento" : "Errore eliminazione"); }
  };
  const uploadImg = async (fid, files) => {
    if (!files || !files.length) return;
    setUploading(fid);
    try {
      const fd = new FormData();
      for (const fl of files) fd.append("files", fl);
      const { data } = await api.post(`/exams/${fid}/images/upload`, fd);
      toast.success(`${data.added} immagine/i caricata/e`); reload();
    } catch (e) {
      const st = e?.response?.status;
      if (st === 403) toast.error("Accesso non consentito");
      else if (st === 507) toast.error("Spazio PAM Drive esaurito. Libera spazio prima di caricare nuovi contenuti.");
      else if (st === 413) toast.error("File troppo grande (max 100MB).");
      else toast.error("Caricamento non completato. Uno o più file non sono validi. Nessuna immagine è stata caricata.");
    }
    finally { setUploading(null); }
  };
  const openDocument = async (f, d) => {
    setOpening(d.doc_id);
    try {
      const url = `/exams/${f.id}/clinical-documents/${d.id}/view`;
      const { data, headers } = await api.get(url, { responseType: "blob" });
      const ct = (headers["content-type"] || d.format || "application/pdf").split(";")[0];
      if (ct === "text/html" || d.format === "html") setViewer({ url: URL.createObjectURL(new Blob([data], { type: "text/html" })), filename: d.filename, contentType: "text/html" });
      else setViewer({ blob: new Blob([data], { type: "application/pdf" }), filename: d.filename, contentType: "application/pdf" });
    } catch (e) { toast.error(e?.response?.status === 403 ? "Accesso non consentito" : "Impossibile aprire il documento"); }
    finally { setOpening(null); }
  };

  return (<Layout><div className="yp-container py-8" data-testid="clinical-folder">
    {me && me.role === "organization" && !canClinical(me) && <Navigate to="/" replace />}
    <div className="mb-4">
      <Link to="/drive" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-2" data-testid="clinical-back"><ChevronRight size={14} className="rotate-180" />{t("pd.tab_patients")}</Link>
      <div className="flex items-center gap-3">
        {patient && <Avatar url={patient.avatar} label={patient.name} cls="w-12 h-12 text-base" />}
        <h1 className="text-3xl font-bold" data-testid="clinical-title">{t("pd.fascicolo")}{patient?.name ? ` — ${patient.name}` : ""}</h1>
        {access?.authorized && <span className={`text-xs px-2 py-0.5 rounded-full ${isInternal ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="clinical-enroll-badge">{isInternal ? t("diag.internal_badge") : t("diag.enrolled_badge")}</span>}
      </div>
      <p className="text-sm text-slate-500 mt-1">{t("pd.clin_subtitle")}</p>
      {access?.authorized && <div className="flex gap-2 mt-3 flex-wrap" data-testid="clinical-actions">
        {!isInternal && <Link to={`/chat/${patientId}`} className="yp-btn yp-btn-outline !py-1.5 !px-3 text-sm" data-testid="clinical-chat-btn">{t("followx.open_chat")}</Link>}
        {!isInternal && <Link to={canClinical(me) ? `/bio/${patientId}` : `/org-bio/${patientId}`} className="yp-btn yp-btn-outline !py-1.5 !px-3 text-sm" data-testid="clinical-bio-btn">{canClinical(me) ? t("followx.open_bio") : t("followx.bio")}</Link>}
        {canClinical(me) && <button className="yp-btn yp-btn-primary !py-1.5 !px-3 text-sm" onClick={() => setNewEp({ type: "visita", date: new Date().toISOString().slice(0, 10), title: "" })} data-testid="clinical-new-episode-btn">{t("pd.new_episode")}</button>}
      </div>}</div>

    {/* Ricerca e filtri */}
    {folders.length > 0 && (
      <div className="flex gap-2 mb-4 flex-wrap items-center" data-testid="clinical-filters">
        <input className="yp-input max-w-xs" placeholder={t("pd.clin_search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="clinical-search" />
        <select className="yp-input !w-auto text-sm" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="clinical-period">
          <option value="all">{t("pd.period_all")}</option>
          <option value="7">{t("pd.period_7")}</option>
          <option value="30">{t("pd.period_30")}</option>
          <option value="365">{t("pd.period_365")}</option>
        </select>
        <select className="yp-input !w-auto text-sm" value={typeF} onChange={(e) => setTypeF(e.target.value)} data-testid="clinical-type">
          <option value="all">{t("pd.clin_all")}</option>
          {types.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
        </select>
      </div>)}

    {folders.length === 0 && <div className="text-slate-400" data-testid="clinical-empty">Nessun fascicolo condiviso.</div>}
    {folders.length > 0 && filtered.length === 0 && <div className="text-slate-400" data-testid="clinical-no-results">{t("pd.no_results")}</div>}

    <div className="space-y-2" data-testid="clinical-list">
      {filtered.map((f) => {
        const isOpen = openId === f.id;
        return (
        <div key={f.id} className="yp-card overflow-hidden" data-testid="clinical-item">
          {/* Riga sintetica (sempre visibile) */}
          <button className="w-full flex items-center gap-3 p-4 text-left" onClick={() => setOpenId(isOpen ? null : f.id)} data-testid="clinical-exam-toggle">
            {isOpen ? <ChevronDown size={18} className="text-blue-500 shrink-0" /> : <ChevronRight size={18} className="text-slate-400 shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="font-medium break-words">{f.title}</div>
              <div className="text-xs text-slate-500 break-words">{fmtDate(f.study_date, i18n.language)} · {f.exam_type}
                {f.provider_label ? ` · ${f.provider_label}` : ""}
              </div>
            </div>
            <div className="text-xs text-slate-600 shrink-0 text-right">
              <div>🖼 {f.images_count}</div>
              <div>📄 {(f.documents || []).length || f.documents_count || 0}</div>
            </div>
          </button>

          {/* Dettaglio (solo se aperto) */}
          {isOpen && (
          <div className="px-4 pb-4 border-t border-slate-100" data-testid="clinical-exam-detail">
            {(f.linked_professional || f.linked_organization) && (
              <div className="text-[11px] text-slate-500 mt-2 break-words" data-testid="clinical-item-subjects">
                {f.linked_professional && <span>Professionista: {f.linked_professional.name}</span>}
                {f.linked_professional && f.linked_organization && <span> · </span>}
                {f.linked_organization && <span>Struttura: {f.linked_organization.name}</span>}
              </div>)}
            <div className="mt-3 flex flex-wrap gap-2">
              {f.images_count > 0 && <button className="yp-btn yp-btn-primary !py-1 !px-3 text-xs" onClick={() => openFolder(f)} data-testid="clinical-open-btn">Apri esame</button>}
              <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={() => setEditorCtx({ folderId: f.id })} data-testid="clinical-redact-btn">Redigi documento clinico</button>
              <label className="yp-btn yp-btn-outline !py-1 !px-3 text-xs cursor-pointer" data-testid="clinical-upload-btn">
                {uploading === f.id ? "Caricamento…" : "Carica documento clinico"}
                <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => { uploadDoc(f.id, e.target.files[0]); e.target.value = ""; }} data-testid="clinical-upload-input" />
              </label>
              <label className="yp-btn yp-btn-outline !py-1 !px-3 text-xs cursor-pointer" data-testid="clinical-upload-image-btn">
                {uploading === f.id ? "Caricamento…" : "Carica immagine"}
                <input type="file" accept=".dcm,application/dicom,image/jpeg,image/png" multiple className="hidden" onChange={(e) => { uploadImg(f.id, Array.from(e.target.files)); e.target.value = ""; }} data-testid="clinical-upload-image-input" />
              </label>
            </div>
            {(f.documents || []).length > 0 && (
              <div className="mt-3 border-t border-slate-100 pt-2" data-testid="clinical-documents">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">Documenti clinici</div>
                {(f.documents || []).map((d) => (
                  <div key={d.doc_id} className="flex items-start justify-between gap-2 text-xs py-0.5" data-testid="clinical-document-row">
                    <div className="min-w-0 flex-1">
                      <div className="break-words font-medium" title={d.filename}>{d.filename}{d.is_draft ? " (bozza)" : ""}</div>
                      {(d.author_name || f.provider_label) && <div className="text-[10px] text-slate-400 break-words" data-testid="clinical-document-author">Autore: {d.author_name || f.provider_label}{d.published_at ? ` · ${String(d.published_at).slice(0, 10)}` : ""}</div>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {d.can_edit && <button className="yp-btn yp-btn-ghost !py-0.5 !px-2 text-xs" onClick={() => setEditorCtx({ fid: f.id, id: d.id })} data-testid="clinical-doc-edit-btn">Modifica</button>}
                      <button className="yp-btn yp-btn-ghost !py-0.5 !px-2 text-xs" onClick={() => openDocument(f, d)} disabled={opening === d.doc_id} data-testid="clinical-doc-btn">Apri</button>
                      {d.can_delete && <button className="yp-btn yp-btn-ghost !py-0.5 !px-2 text-xs text-red-600" onClick={() => deleteDoc(f.id, d)} data-testid="clinical-doc-delete-btn">Elimina</button>}
                    </div>
                  </div>))}
              </div>)}
          </div>)}
        </div>);
      })}
    </div>
    {viewer?.contentType === "application/pdf" && <PdfViewer blob={viewer.blob} filename={viewer.filename} onClose={() => setViewer(null)} testid="clinical-pdf-viewer" />}
    {viewer && viewer.contentType === "text/html" && (
      <div className="fixed inset-0 z-50 bg-black/70 flex flex-col" onClick={() => setViewer(null)} data-testid="clinical-viewer-modal">
        <div className="flex justify-between items-center p-3 text-white" onClick={(e) => e.stopPropagation()}>
          <span className="text-sm truncate">{viewer.filename}</span>
          <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-white" onClick={() => setViewer(null)} data-testid="clinical-viewer-close">✕</button>
        </div>
        <div className="flex-1 p-4 overflow-auto" onClick={(e) => e.stopPropagation()}>
          <iframe src={viewer.url} sandbox="" title={viewer.filename} className="w-full h-full bg-white rounded" data-testid="clinical-viewer-html" />
        </div>
      </div>)}
    {imgViewer && <DicomViewer items={imgViewer.items} folderId={imgViewer.folderId} onDeleteImage={(oid) => deleteImage(imgViewer.folderId, oid)} onClose={() => setImgViewer(null)} onSaved={reload} />}
    {editorCtx && <ClinicalDocEditor ctx={editorCtx.folderId ? { folderId: editorCtx.folderId } : undefined} editDoc={editorCtx.fid ? editorCtx : undefined} onClose={() => setEditorCtx(null)} onSaved={reload} />}
    {newEp && (
      <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => setNewEp(null)} data-testid="new-episode-overlay">
        <div className="bg-white rounded-xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
          <h3 className="font-semibold">Nuovo episodio clinico</h3>
          <select className="yp-input w-full" value={newEp.type} onChange={(e) => setNewEp({ ...newEp, type: e.target.value })} data-testid="new-episode-type">
            <option value="visita">Visita in presenza</option>
            <option value="videoconsulto">Videoconsulto</option>
            <option value="servizio">Servizio</option>
            <option value="documento">Altro</option>
          </select>
          <input type="date" className="yp-input w-full" value={newEp.date} onChange={(e) => setNewEp({ ...newEp, date: e.target.value })} data-testid="new-episode-date" />
          <input className="yp-input w-full" placeholder="Titolo (facoltativo)" value={newEp.title} onChange={(e) => setNewEp({ ...newEp, title: e.target.value })} data-testid="new-episode-title" />
          <div className="flex justify-end gap-2">
            <button className="yp-btn yp-btn-ghost" onClick={() => setNewEp(null)} data-testid="new-episode-cancel">Annulla</button>
            <button className="yp-btn yp-btn-primary" data-testid="new-episode-create" onClick={async () => {
              try { const { data } = await api.post("/clinical-episodes", { patient_id: patientId, episode_type: newEp.type, date: newEp.date, name: newEp.title || undefined });
                setOpenId(data.folder_id); setNewEp(null); reload(); toast.success("Episodio creato"); }
              catch (e) { toast.error(e?.response?.data?.detail === "no_relationship" ? "Nessuna relazione con il paziente" : "Errore"); }
            }}>Crea e apri</button>
          </div>
        </div>
      </div>)}
    {opening && !viewer && (
      <div className="fixed inset-0 z-[75] bg-black/60 flex items-center justify-center" data-testid="clinical-loading-overlay">
        <div className="bg-white rounded-xl px-6 py-4 flex items-center gap-3 shadow-lg">
          <span className="inline-block w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-slate-700">Caricamento documento…</span>
        </div>
      </div>)}
  </div></Layout>);
}
